<script setup lang="ts">
import { computed } from 'vue'
import { formatDisplayDate, formatToInternational } from '@shared/utils'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import { formatMarketTokenPrice } from '../../trading'
import SellSummaryCard, { type SellSummaryRow } from './SellSummaryCard.vue'
import SellNote from './SellNote.vue'
import { CANNOT_SELL_TITLE, EXIT_OVERVIEW_NOTE, NOT_TRADABLE_NOTE } from '../constants/sellCopy'

// Sell — Exit overview (spec 1.7.3): before any quantity, what the holding is
// worth today against what it cost — current price per token, weighted-average
// cost, appreciation — and how many tokens can be listed. When the holding
// can't be listed the reasons replace the way forward.

interface Props {
  /** Latest valuation per token; null when there is none. */
  valuation: number | null
  valuationDate: string | null
  averageCost: number
  appreciationPerToken: number | null
  appreciationPct: number | null
  tokensHeld: number
  tokensLocked: number
  tokensInLockIn: number
  lockInEndsAt: string | null
  tokensSellable: number
  isTradable: boolean
  /** Why the holding can't be listed (spec copy), empty when it can. */
  ineligibleReasons: string[]
}

const props = defineProps<Props>()

const date = (iso: string | null) => (iso ? formatDisplayDate(new Date(iso)) : '-')

const signedPct = (pct: number) => `${pct > 0 ? '+' : ''}${pct.toFixed(2)}%`

const appreciation = computed(() => {
  if (props.appreciationPerToken === null) return '-'
  const value = formatMarketTokenPrice(Math.abs(props.appreciationPerToken))
  const sign = props.appreciationPerToken < 0 ? '− ' : '+ '
  const pct = props.appreciationPct !== null ? ` (${signedPct(props.appreciationPct)})` : ''
  return `${sign}${value}${pct}`
})

const rows = computed<SellSummaryRow[]>(() => {
  const list: SellSummaryRow[] = [
    {
      label: 'Current price per token',
      value: props.valuation !== null ? formatMarketTokenPrice(props.valuation) : 'Unavailable',
      info: {
        title: 'Current price per token',
        note: `Latest independent valuation · ${date(props.valuationDate)}`,
        description:
          'What one token is worth at the latest DLD-registered valuation, refreshed every quarter. Listing prices are set around it.',
      },
    },
    {
      label: 'Your average cost per token',
      value: formatMarketTokenPrice(props.averageCost),
      info: {
        title: 'Average cost per token',
        note: 'Weighted across every purchase of this property',
        description:
          'What you paid per token on average. The performance fee only applies to what you sell above it.',
      },
    },
    {
      label: 'Appreciation per token',
      value: appreciation.value,
      tone:
        props.appreciationPerToken === null
          ? 'default'
          : props.appreciationPerToken >= 0
            ? 'success'
            : 'danger',
    },
    { label: 'Tokens held', value: formatToInternational(props.tokensHeld) },
  ]
  if (props.tokensLocked > 0) {
    list.push({
      label: 'Locked in open listings',
      value: formatToInternational(props.tokensLocked),
    })
  }
  if (props.tokensInLockIn > 0) {
    list.push({
      label: 'In 3-month lock-in',
      value: `${formatToInternational(props.tokensInLockIn)} · until ${date(props.lockInEndsAt)}`,
      tone: 'warning',
    })
  }
  list.push({
    label: 'Tokens you can sell',
    value: formatToInternational(props.tokensSellable),
    isTotal: true,
  })
  return list
})
</script>

<template>
  <Box direction="col" gap="4">
    <Text
      is="h3"
      type="caption"
      weight="bold"
      color="var(--p-sliceal-gray-400)"
      class="sell-overview__eyebrow"
      >EXIT OVERVIEW</Text
    >

    <SellSummaryCard :rows="rows" />

    <SellNote
      v-if="!isTradable"
      tone="danger"
      icon="exclamation-circle"
      :title="NOT_TRADABLE_NOTE.title"
      :text="NOT_TRADABLE_NOTE.text"
    />
    <template v-else-if="ineligibleReasons.length">
      <SellNote
        v-for="(reason, index) in ineligibleReasons"
        :key="reason"
        tone="warning"
        icon="exclamation-circle"
        :title="index === 0 ? CANNOT_SELL_TITLE : undefined"
        :text="reason"
      />
    </template>
    <SellNote v-else tone="neutral" icon="info-circle" :text="EXIT_OVERVIEW_NOTE" />
  </Box>
</template>

<style scoped>
.sell-overview__eyebrow {
  margin: 0;
  line-height: 1.4;
}
</style>
